// The app still answers on its pre-rebrand address, app.kf49citrus.com, beside
// bigagri.app, with no redirect between them. Each address keeps its OWN
// sign-in and its OWN offline queue (IndexedDB is per origin), and every link
// we send opens bigagri.app. A scout who captured on one address and signed in
// on the other saw nothing waiting (Magalies, Oct 2026).
//
// So the old address hands people over to the new one: at start-up only (never
// mid-form), only online, and only when this phone has nothing waiting to
// upload — a phone with queued work stays until the queue is empty, so nothing
// is stranded on the old origin. Unattended devices are never moved: a paired
// capture device, a face-clock kiosk, or a kiosk / label / monitor / scan
// screen. Their pairing lives in this origin's storage and a move would unpair
// them (rebrand plan WS2, docs/plans/REBRAND-MIGRATION-PLAN.md).

export const OLD_APP_HOST = 'app.kf49citrus.com'
export const NEW_APP_ORIGIN = 'https://bigagri.app'

// Screens that run unattended or are paired to this origin. Prefix match.
const STAY_ROUTES = [
  '/clock-face',
  '/device-signin',
  '/device-capture',
  '/packer-capture',
  '/label',
  '/label-designer',
  '/monitor',
  '/harvest/monitor',
  '/farm/drain-monitoring',
  '/stock/scan',
  // Sign-in with Google / Microsoft returns here with a PKCE code whose
  // verifier is in THIS origin's storage; moved, the exchange fails.
  '/auth/callback',
  // Headless print renders (api/render-pdf) load these and wait for the page.
  '/render',
] as const

// localStorage keys that mark this browser as a kiosk or a device with work of
// its own (src/modules/shared/ClockFace.tsx, src/lib/deviceCaptureQueue.ts).
const KIOSK_TOKEN_KEY      = 'kf49:clock-face:token'
const DEVICE_CAPTURE_QUEUE = 'kf49.device.capture-queue'
// A shared floor tablet set up with ?kiosk=1 (src/components/KioskIdleLogout.tsx):
// moved, it loses its idle sign-out and the next person inherits a session.
const SHARED_KIOSK_KEY     = 'kf49.kioskMode'
// A paired floor device's own sign-in (src/lib/deviceSession.ts).
const DEVICE_TOKEN_KEY     = 'kf49.device.token'

/** localStorage keys whose presence marks this browser as a SHARED device
 *  (kiosk, paired floor tablet). The ONE list: src/lib/appearance.ts keeps such
 *  a browser dark, and index.html's boot script repeats these keys (a test keeps
 *  the two in step). */
export const SHARED_DEVICE_MARKERS = [KIOSK_TOKEN_KEY, DEVICE_TOKEN_KEY, SHARED_KIOSK_KEY] as const

// Unsent work that is NOT in the offline queue: in-progress drafts a page offers
// to resume after a reload. Moved, the draft stays behind on the old origin and
// is never offered again. Prefix match on the localStorage key.
const DRAFT_KEY_PREFIXES = [
  'kf49.spray.trackDraft.',     // GPS track of a spray in progress (src/lib/sprayDraft.ts)
  'kf49.scout.walkDraft.',      // GPS scout walk in progress (src/lib/walkDraft.ts)
  'harvest.capture.session.',   // who is present on today's harvest capture (HarvestCapture.tsx)
  'count-sheet.',               // a stock count being typed (src/modules/farm/useCountSheetDraft.ts)
] as const
const EMPTY_DRAFT_VALUES = new Set(['', '""', '{}', '[]', 'null'])

// A queue read that never answers (an IndexedDB open can hang on some WebKit
// builds) must not leave the old address on a blank screen: give up and stay.
const QUEUE_COUNT_TIMEOUT_MS = 3000

export type MoveDecision =
  | { move: false; reason: 'not-old-host' | 'offline' | 'device-screen' | 'paired-device' | 'kiosk' | 'shared-kiosk' | 'device-queue' | 'draft-waiting' | 'queue-waiting' | 'queue-unreadable' }
  | { move: true; target: string }

export interface MoveContext {
  hostname:     string
  pathname:     string
  search:       string
  hash:         string
  online:       boolean
  pairedDevice: () => boolean
  readLocal:    (key: string) => string | null
  localKeys:    () => string[]
  queueCount:   () => Promise<number>
  timeoutMs?:   number
}

function onStayRoute(pathname: string): boolean {
  return STAY_ROUTES.some(r => pathname === r || pathname.startsWith(`${r}/`))
}

function deviceQueueHasWork(raw: string | null): boolean {
  if (!raw) return false
  try {
    const parsed: unknown = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.length > 0 : true
  } catch {
    return true // unreadable = assume work is there; never move on a guess
  }
}

function hasDraft(ctx: MoveContext): boolean {
  let keys: string[]
  try { keys = ctx.localKeys() } catch { return true } // can't look = assume there is one
  return keys.some(k =>
    DRAFT_KEY_PREFIXES.some(p => k.startsWith(p)) && !EMPTY_DRAFT_VALUES.has((ctx.readLocal(k) ?? '').trim()))
}

function countWithin(count: () => Promise<number>, ms: number): Promise<number> {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new Error('queue count timed out')), ms)
    count().then(n => { clearTimeout(t); resolve(n) }, e => { clearTimeout(t); reject(e) })
  })
}

export async function decideOldAddressMove(ctx: MoveContext): Promise<MoveDecision> {
  if (ctx.hostname !== OLD_APP_HOST) return { move: false, reason: 'not-old-host' }
  if (!ctx.online) return { move: false, reason: 'offline' }
  if (onStayRoute(ctx.pathname)) return { move: false, reason: 'device-screen' }
  if (ctx.pairedDevice()) return { move: false, reason: 'paired-device' }
  if (ctx.readLocal(KIOSK_TOKEN_KEY)) return { move: false, reason: 'kiosk' }
  if (ctx.readLocal(SHARED_KIOSK_KEY) === '1') return { move: false, reason: 'shared-kiosk' }
  if (deviceQueueHasWork(ctx.readLocal(DEVICE_CAPTURE_QUEUE))) return { move: false, reason: 'device-queue' }
  if (hasDraft(ctx)) return { move: false, reason: 'draft-waiting' }
  let waiting: number
  try {
    waiting = await countWithin(ctx.queueCount, ctx.timeoutMs ?? QUEUE_COUNT_TIMEOUT_MS)
  } catch {
    return { move: false, reason: 'queue-unreadable' }
  }
  if (waiting > 0) return { move: false, reason: 'queue-waiting' }
  return { move: true, target: `${NEW_APP_ORIGIN}${ctx.pathname}${ctx.search}${ctx.hash}` }
}
